import type { EmblaCarouselType } from 'embla-carousel';
import type { CSSProperties, FC } from 'react';
import { motion } from 'motion/react';
import { useCallback, useEffect, useState } from 'react';
import styles from './CarouselDots.module.css';

const DOT_SPACING = 32;
const DOT_HEIGHT = 16;
const DOT_WIDTH_INACTIVE = 16;
const DOT_WIDTH_ACTIVE = 24;

const containerStyle = {
    '--dot-height': `${DOT_HEIGHT}px`,
    '--dot-width': `${DOT_WIDTH_INACTIVE}px`
} as CSSProperties;

type Props = {
    emblaApi: EmblaCarouselType | undefined;
};

export const CarouselDots: FC<Props> = ({ emblaApi }) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [slideCount, setSlideCount] = useState(0);

    const handleSelect = useCallback((emblaApi: EmblaCarouselType) => {
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, []);

    const handleDotClick = useCallback((index: number) => {
        emblaApi?.scrollTo(index);
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi)
            return;

        setSlideCount(emblaApi.scrollSnapList().length);
        handleSelect(emblaApi);

        emblaApi.on('reInit', handleSelect).on('select', handleSelect);
    }, [emblaApi, handleSelect]);

    return (
        <div className={styles.container} style={containerStyle}>
            <motion.div
                className={styles.activeDot}
                initial={false}
                animate={{
                    x: selectedIndex * DOT_SPACING,
                    width: DOT_WIDTH_ACTIVE,
                    height: DOT_HEIGHT
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            />
            <div className={styles.inactiveDotsWrapper}>
                {Array.from({ length: slideCount }).map((_, i) => (
                    <div
                        key={i}
                        className={styles.inactiveDot}
                        style={i === selectedIndex ? { opacity: 0, width: DOT_WIDTH_ACTIVE } : { opacity: 0.2 }}
                        onClick={() => handleDotClick(i)}
                    />
                ))}
            </div>
        </div>
    );
};
